import React from 'react';
import {
  Info,
  AlertCircle,
  CheckSquare
} from 'lucide-react';

interface BusinessRulesEditorProps {
  currentRules: {
    strategy: 'sequential' | 'synthetic' | 'single';
    syntheticRunCount?: number;
    runIdentifier?: string;
  };
  validation: {
    requiredColumns: string[];
    dataQualityChecks: Array<{
      field: string;
      rule: 'not_null' | 'positive' | 'date_range' | 'regex';
      params?: any;
    }>;
  };
  onRulesChange: (runHandling: any, validation: any) => void;
}

const BusinessRulesEditor: React.FC<BusinessRulesEditorProps> = ({
  currentRules,
  validation,
  onRulesChange
}) => {
  const handleRunStrategyChange = (strategy: string) => {
    const newRules = { ...currentRules, strategy };

    // Reset strategy-specific fields
    if (strategy !== 'synthetic') {
      delete newRules.syntheticRunCount;
    }
    if (strategy !== 'sequential') {
      delete newRules.runIdentifier;
    }

    onRulesChange(newRules, validation);
  };

  const handleSyntheticCountChange = (count: string) => {
    const numCount = parseInt(count) || 3;
    onRulesChange(
      { ...currentRules, syntheticRunCount: numCount },
      validation
    );
  };

  const handleRunIdentifierChange = (identifier: string) => {
    onRulesChange(
      { ...currentRules, runIdentifier: identifier },
      validation
    );
  };

  const [selectedValidations, setSelectedValidations] = React.useState<string[]>([
    'loan_id_required',
    'positive_amounts',
    'valid_dates',
    'broker_required'
  ]);

  const handleValidationToggle = (validationId: string) => {
    setSelectedValidations(prev =>
      prev.includes(validationId)
        ? prev.filter(v => v !== validationId)
        : [...prev, validationId]
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-6">
        <h3 className="text-white font-medium text-lg mb-4">Run Handling Strategy</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              How are commission runs structured in this file?
            </label>
            <select
              value={currentRules.strategy}
              onChange={e => handleRunStrategyChange(e.target.value)}
              className="w-full px-4 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
            >
              <option value="sequential">Sequential Runs (e.g., Run 1, Run 2, Run 3 per month)</option>
              <option value="synthetic">Single File (needs synthetic run splitting)</option>
              <option value="single">Single Run per File</option>
            </select>
            <p className="text-xs text-slate-500 mt-1">
              Different aggregators structure their files differently
            </p>
          </div>

          {currentRules.strategy === 'synthetic' && (
            <>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-blue-400 font-medium">Synthetic Run Splitting</h4>
                    <p className="text-slate-400 text-sm mt-1">
                      This aggregator provides a single monthly file that needs to be split into multiple runs for consistent reporting.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Number of Synthetic Runs
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={currentRules.syntheticRunCount || 3}
                  onChange={e => handleSyntheticCountChange(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  How many runs should we create from each file?
                </p>
              </div>

              <div className="bg-slate-800/30 rounded-lg p-4">
                <h5 className="text-slate-300 font-medium mb-2">How Synthetic Splitting Works</h5>
                <ul className="text-slate-400 text-sm space-y-1">
                  <li>• Records are distributed evenly across {currentRules.syntheticRunCount || 3} runs</li>
                  <li>• Distribution is deterministic based on loan ID hash</li>
                  <li>• Same loan always goes to same run number</li>
                  <li>• Ensures consistent month-over-month comparisons</li>
                </ul>
              </div>
            </>
          )}

          {currentRules.strategy === 'sequential' && (
            <>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-blue-400 font-medium">Sequential Run Detection</h4>
                    <p className="text-slate-400 text-sm mt-1">
                      This aggregator provides multiple runs per month (e.g., weekly or fortnightly).
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Run Identifier Column (Optional)
                </label>
                <input
                  type="text"
                  value={currentRules.runIdentifier || ''}
                  onChange={e => handleRunIdentifierChange(e.target.value)}
                  placeholder="e.g., 'Run Number' or 'Pay Period'"
                  className="w-full px-4 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Column that contains the run number or identifier
                </p>
              </div>

              <div className="bg-slate-800/30 rounded-lg p-4">
                <p className="text-slate-400 text-sm">
                  <span className="text-slate-300 font-medium">Automatic Run Detection:</span> If no identifier column is specified, runs will be detected based on upload date and sequence.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-6">
        <h3 className="text-white font-medium text-lg mb-4">Data Quality Rules</h3>

        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-blue-400 font-medium">Validation Rules</h4>
              <p className="text-slate-400 text-sm mt-1">
                Define rules to ensure data quality during ingestion
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <h4 className="text-slate-300 font-medium mb-3">Standard Validations</h4>
            <div className="space-y-2">
              {[
                { id: 'loan_id_required', label: 'Loan ID must be present' },
                { id: 'positive_amounts', label: 'Commission amounts must be positive or zero' },
                { id: 'valid_dates', label: 'Settlement dates must be valid' },
                { id: 'broker_required', label: 'Broker name must be present' },
                { id: 'duplicate_check', label: 'Duplicate loan detection within upload' },
                { id: 'gst_validation', label: 'GST validation (10% of ex-GST amount)' }
              ].map(validation => (
                <label key={validation.id} className="flex items-center gap-3 p-2 hover:bg-slate-800/30 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedValidations.includes(validation.id)}
                    onChange={() => handleValidationToggle(validation.id)}
                    className="w-4 h-4 text-blue-600 bg-slate-800 border-slate-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-slate-300 text-sm">{validation.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-700 pt-6">
            <h4 className="text-slate-300 font-medium mb-3">Advanced Rules</h4>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Maximum loan amount (millions)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="100"
                    defaultValue="20"
                    className="flex-1 px-4 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                  <span className="text-slate-400">M</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Valid settlement date range
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    className="flex-1 px-4 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                  <span className="text-slate-400">to</span>
                  <input
                    type="date"
                    placeholder="Leave empty for today"
                    className="flex-1 px-4 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Leave end date empty to use today's date</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Required commission types
                </label>
                <div className="space-y-2">
                  {[
                    { id: 'upfront_required', label: 'Must have upfront commissions' },
                    { id: 'trail_required', label: 'Must have trail commissions' },
                    { id: 'deductions_required', label: 'Must have broker deductions' }
                  ].map(validation => (
                    <label key={validation.id} className="flex items-center gap-3 p-2 hover:bg-slate-800/30 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-blue-600 bg-slate-800 border-slate-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-slate-300 text-sm">{validation.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-6">
        <h3 className="text-white font-medium text-lg mb-4">Business Logic</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              GST Handling
            </label>
            <select
              defaultValue="included"
              className="w-full px-4 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
            >
              <option value="included">File includes GST amounts</option>
              <option value="excluded">File excludes GST (calculate at 10%)</option>
              <option value="mixed">Mixed (check per record)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Deduction Handling
            </label>
            <select
              defaultValue="transform"
              className="w-full px-4 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
            >
              <option value="transform">Transform positive deductions to negative</option>
              <option value="preserve">Preserve original sign (already negative)</option>
              <option value="absolute">All amounts are absolute (determine sign from payment type)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Commission Rate Calculation
            </label>
            <select
              defaultValue="provided"
              className="w-full px-4 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
            >
              <option value="provided">Use provided commission rate</option>
              <option value="calculate">Calculate from amount and loan balance</option>
              <option value="fixed">Use fixed rates by payment type</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <CheckSquare className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-green-400 font-medium">Template Behavior</h4>
            <p className="text-slate-400 text-sm mt-1">
              These rules determine how files from this aggregator are processed. They ensure consistent handling across all uploads and automatic error detection.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessRulesEditor;